/**
 * CrmMenuManager — admin control for the public top navigation menu.
 * Add / edit / delete / reorder (drag) items, one-level dropdown sub-items,
 * visibility toggle, CTA button (with colours), and menu styling (font, size,
 * weight, colours, background, sticky, alignment, logo). Live preview + Save.
 * Persists to /api/admin/landing/menu; the public Header renders from /api/menu.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Reorder, useDragControls } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  GripVertical, Trash2, Plus, Save, ChevronDown, ChevronRight, Eye, EyeOff,
  Menu as MenuIcon, Palette, ExternalLink, CornerDownRight,
} from 'lucide-react';
import { invalidateMenuCache } from '../../hooks/useMenuConfig';
import { FONT_FAMILIES, resolveFontFamily } from '../../lib/fonts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = { withCredentials: true };
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`);

const SelfTarget = ({ value, onChange, testid }) => (
  <select value={value || '_self'} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white" data-testid={testid}>
    <option value="_self">Same tab</option>
    <option value="_blank">New tab</option>
  </select>
);

const ChildRow = ({ child, onChange, onDelete }) => (
  <div className="flex items-center gap-2 pl-6 py-1.5" data-testid={`menu-child-${child.id}`}>
    <CornerDownRight className="w-3.5 h-3.5 text-[#B7B3AC] shrink-0" />
    <Input value={child.label} onChange={(e) => onChange({ ...child, label: e.target.value })} placeholder="Label" className="h-8 flex-1" data-testid={`menu-child-label-${child.id}`} />
    <Input value={child.link} onChange={(e) => onChange({ ...child, link: e.target.value })} placeholder="/link" className="h-8 flex-1" data-testid={`menu-child-link-${child.id}`} />
    <button type="button" onClick={() => onChange({ ...child, visible: !(child.visible !== false) })} className="p-1.5" title="Visibility" data-testid={`menu-child-visible-${child.id}`}>
      {child.visible !== false ? <Eye className="w-4 h-4 text-[#06D6A0]" /> : <EyeOff className="w-4 h-4 text-[#B7B3AC]" />}
    </button>
    <button type="button" onClick={onDelete} className="p-1.5 text-[#FF3B6B]" data-testid={`menu-child-del-${child.id}`}><Trash2 className="w-4 h-4" /></button>
  </div>
);

const ItemRow = ({ item, onChange, onDelete }) => {
  const controls = useDragControls();
  const [open, setOpen] = useState(false);
  const setChild = (i, c) => onChange({ ...item, children: (item.children || []).map((x, idx) => (idx === i ? c : x)) });
  const delChild = (i) => onChange({ ...item, children: (item.children || []).filter((_, idx) => idx !== i) });
  const addChild = () => onChange({ ...item, children: [...(item.children || []), { id: uid(), label: 'New link', link: '/', target: '_self', visible: true }] });
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls} className="rounded-xl border border-[#E8E5DF] bg-white" data-testid={`menu-item-${item.id}`}>
      <div className="flex items-center gap-2 p-2.5">
        <button type="button" onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-[#B7B3AC] touch-none shrink-0" title="Drag to reorder" data-testid={`menu-drag-${item.id}`}><GripVertical className="w-4 h-4" /></button>
        <button type="button" onClick={() => setOpen((o) => !o)} className="shrink-0" data-testid={`menu-expand-${item.id}`}>{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <Input value={item.label} onChange={(e) => onChange({ ...item, label: e.target.value })} placeholder="Label" className="h-8 flex-1 min-w-0" data-testid={`menu-label-${item.id}`} />
        <Input value={item.link} onChange={(e) => onChange({ ...item, link: e.target.value })} placeholder="/link" className="h-8 flex-1 min-w-0" data-testid={`menu-link-${item.id}`} />
        <button type="button" onClick={() => onChange({ ...item, visible: !(item.visible !== false) })} className="p-1.5 shrink-0" title="Visibility" data-testid={`menu-visible-${item.id}`}>
          {item.visible !== false ? <Eye className="w-4 h-4 text-[#06D6A0]" /> : <EyeOff className="w-4 h-4 text-[#B7B3AC]" />}
        </button>
        <button type="button" onClick={onDelete} className="p-1.5 text-[#FF3B6B] shrink-0" title="Delete" data-testid={`menu-del-${item.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="border-t border-[#E8E5DF] p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-4">
            <SelfTarget value={item.target} onChange={(v) => onChange({ ...item, target: v })} testid={`menu-target-${item.id}`} />
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={!!item.is_cta} onCheckedChange={(v) => onChange({ ...item, is_cta: v })} data-testid={`menu-cta-${item.id}`} />
              CTA button
            </label>
            {item.is_cta && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-[#6B6B7A]">Button
                  <input type="color" value={item.cta_bg || '#FF3B6B'} onChange={(e) => onChange({ ...item, cta_bg: e.target.value })} className="h-8 w-10 rounded border border-[#E8E5DF]" data-testid={`menu-cta-bg-${item.id}`} />
                </label>
                <label className="flex items-center gap-1.5 text-xs text-[#6B6B7A]">Text
                  <input type="color" value={item.cta_color || '#FFFFFF'} onChange={(e) => onChange({ ...item, cta_color: e.target.value })} className="h-8 w-10 rounded border border-[#E8E5DF]" data-testid={`menu-cta-color-${item.id}`} />
                </label>
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-[#6B6B7A]">Dropdown sub-items</span>
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={addChild} data-testid={`menu-add-child-${item.id}`}><Plus className="w-3.5 h-3.5 mr-1" />Sub-item</Button>
            </div>
            {(item.children || []).map((c, i) => (
              <ChildRow key={c.id} child={c} onChange={(nc) => setChild(i, nc)} onDelete={() => delChild(i)} />
            ))}
            {(item.children || []).length === 0 && <p className="text-xs text-[#B7B3AC] pl-6">No sub-items — this is a top-level link.</p>}
          </div>
        </div>
      )}
    </Reorder.Item>
  );
};

const PreviewNav = ({ items, style }) => {
  const font = resolveFontFamily(style.font, '"Poppins", sans-serif');
  const sizeCls = { sm: 'text-sm', base: 'text-[15px]', lg: 'text-lg' }[style.size] || 'text-[15px]';
  const alignCls = { left: 'justify-start', center: 'justify-center', right: 'justify-end' }[style.align] || 'justify-end';
  const vis = items.filter((i) => i.visible !== false);
  return (
    <div className="rounded-xl border border-[#E8E5DF] overflow-hidden">
      <div className="flex items-center gap-4 px-4 h-16" style={{ background: style.bg_color || '#ffffff' }}>
        {style.show_logo !== false && <span className="text-xl font-extrabold shrink-0" style={{ color: '#FF3B6B', fontFamily: 'Fredoka, sans-serif' }}>Creative Birthdays</span>}
        <div className={`flex-1 flex items-center gap-5 ${alignCls} flex-wrap`}>
          {vis.map((it) => it.is_cta ? (
            <span key={it.id} className={`rounded-full px-4 py-1.5 font-semibold ${sizeCls}`} style={{ background: it.cta_bg || '#FF3B6B', color: it.cta_color || '#fff', fontFamily: font }}>{it.label}</span>
          ) : (
            <span key={it.id} className={`${sizeCls} font-medium inline-flex items-center gap-1`} style={{ color: style.text_color || '#6B6B7A', fontFamily: font, fontWeight: Number(style.weight) || 500 }}>
              {it.label}{(it.children || []).filter((c) => c.visible !== false).length > 0 && <ChevronDown className="w-3.5 h-3.5" />}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const CrmMenuManager = () => {
  const [items, setItems] = useState([]);
  const [style, setStyle] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showStyle, setShowStyle] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/landing/menu`, ax);
      setItems(res.data?.items || []);
      setStyle(res.data?.style || {});
      setDirty(false);
    } catch { toast.error('Could not load menu'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const mark = () => setDirty(true);
  const setItem = (i, it) => { setItems(items.map((x, idx) => (idx === i ? it : x))); mark(); };
  const delItem = (i) => { setItems(items.filter((_, idx) => idx !== i)); mark(); };
  const addItem = () => { setItems([...items, { id: uid(), label: 'New item', link: '/', target: '_self', visible: true, is_cta: false, children: [] }]); mark(); };
  const setStyleField = (k, v) => { setStyle({ ...style, [k]: v }); mark(); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/landing/menu`, { items, style }, ax);
      invalidateMenuCache();
      setDirty(false);
      toast.success('Menu saved — live on your site');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const SEL = 'w-full h-9 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white';

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="menu-manager-page">
      <div className="flex items-center gap-3 flex-wrap mb-5 sticky top-0 z-10 bg-[#FAF8F4]/95 backdrop-blur py-2">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-extrabold flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}><MenuIcon className="w-6 h-6 text-[#FF3B6B]" />Main Menu{dirty && <span className="ml-2 text-xs font-normal text-[#FF7A2E]">● unsaved</span>}</h1>
          <p className="text-sm text-[#6B6B7A]">Control the top navigation on your public site. Drag to reorder, nest sub-items, and style it.</p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF3B6B] hover:underline" data-testid="menu-view-live"><ExternalLink className="w-4 h-4" />View live</a>
        <Button size="sm" className="rounded-full bg-[#FF3B6B] hover:bg-[#e8305c] text-white" onClick={save} disabled={saving || !dirty} data-testid="menu-save"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
      </div>

      {loading ? <div className="py-20 text-center text-[#6B6B7A]">Loading…</div> : (
        <div className="space-y-5">
          <div className="mb-1"><PreviewNav items={items} style={style} /></div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-[#6B6B7A]">Menu items ({items.length})</span>
              <Button size="sm" variant="outline" className="rounded-full" onClick={addItem} data-testid="menu-add-item"><Plus className="w-3.5 h-3.5 mr-1" />Add item</Button>
            </div>
            <Reorder.Group axis="y" values={items} onReorder={(v) => { setItems(v); mark(); }} className="space-y-2" data-testid="menu-item-list">
              {items.map((it, i) => (
                <ItemRow key={it.id} item={it} onChange={(nit) => setItem(i, nit)} onDelete={() => delItem(i)} />
              ))}
            </Reorder.Group>
          </div>

          <div className="rounded-2xl border border-[#E8E5DF] bg-white">
            <button type="button" onClick={() => setShowStyle((s) => !s)} className="w-full flex items-center justify-between p-3.5" data-testid="menu-style-toggle">
              <span className="font-bold flex items-center gap-2 text-[#1E1E24]"><Palette className="w-4 h-4 text-[#FF3B6B]" />Menu Style</span>
              {showStyle ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {showStyle && (
              <div className="border-t border-[#E8E5DF] p-4 grid sm:grid-cols-2 gap-3" data-testid="menu-style-panel">
                <div>
                  <Label className="text-xs">Font</Label>
                  <select value={style.font || ''} onChange={(e) => setStyleField('font', e.target.value)} className={SEL} data-testid="menu-style-font">
                    {FONT_FAMILIES.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Size</Label>
                  <select value={style.size || 'base'} onChange={(e) => setStyleField('size', e.target.value)} className={SEL} data-testid="menu-style-size">
                    <option value="sm">Small</option><option value="base">Medium</option><option value="lg">Large</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Weight</Label>
                  <select value={String(style.weight || '500')} onChange={(e) => setStyleField('weight', e.target.value)} className={SEL} data-testid="menu-style-weight">
                    <option value="400">Normal</option><option value="500">Medium</option><option value="600">Semibold</option><option value="700">Bold</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Alignment</Label>
                  <select value={style.align || 'right'} onChange={(e) => setStyleField('align', e.target.value)} className={SEL} data-testid="menu-style-align">
                    <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Text colour</Label>
                  <div className="flex items-center gap-2"><input type="color" value={style.text_color || '#6B6B7A'} onChange={(e) => setStyleField('text_color', e.target.value)} className="h-9 w-12 rounded border border-[#E8E5DF]" data-testid="menu-style-text" /><Input value={style.text_color || ''} onChange={(e) => setStyleField('text_color', e.target.value)} className="font-mono" /></div>
                </div>
                <div>
                  <Label className="text-xs">Hover colour</Label>
                  <div className="flex items-center gap-2"><input type="color" value={style.hover_color || '#1E1E24'} onChange={(e) => setStyleField('hover_color', e.target.value)} className="h-9 w-12 rounded border border-[#E8E5DF]" data-testid="menu-style-hover" /><Input value={style.hover_color || ''} onChange={(e) => setStyleField('hover_color', e.target.value)} className="font-mono" /></div>
                </div>
                <div>
                  <Label className="text-xs">Background colour</Label>
                  <div className="flex items-center gap-2"><input type="color" value={style.bg_color || '#ffffff'} onChange={(e) => setStyleField('bg_color', e.target.value)} className="h-9 w-12 rounded border border-[#E8E5DF]" data-testid="menu-style-bg" /><Input value={style.bg_color || ''} onChange={(e) => setStyleField('bg_color', e.target.value)} placeholder="(transparent)" className="font-mono" /></div>
                </div>
                <div className="flex items-center gap-6 pt-5">
                  <label className="flex items-center gap-2 text-sm"><Switch checked={style.sticky !== false} onCheckedChange={(v) => setStyleField('sticky', v)} data-testid="menu-style-sticky" />Sticky</label>
                  <label className="flex items-center gap-2 text-sm"><Switch checked={style.show_logo !== false} onCheckedChange={(v) => setStyleField('show_logo', v)} data-testid="menu-style-logo" />Show logo</label>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmMenuManager;
